import React, { useCallback } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import {
  GoogleOAuthProvider,
  CredentialResponse,
} from '@react-oauth/google';
import { enqueueSnackbar } from 'notistack';
import registerWithTokenMutation
  from '../../lib/mutations/registerWithToken.graphql';
import loginWithTokenMutation from '../../lib/mutations/loginWithToken.graphql';
import { Provider, Registrar } from '../../lib/graphql/api/graphql';
import { providers } from './SocialLoginButton';
import GoogleOneTapLogin from './GoogleOneTapLogin';

const GoogleOneTapProvider = () => {
  const client = useApolloClient();

  const [
    createSession,
  ] = useMutation(loginWithTokenMutation, {
    onCompleted: (data) => {
      localStorage.setItem('accessToken', data.loginWithToken.accessToken);
      client.resetStore();
      window.plausible('Login', {
        props: {
          provider: Provider.GOOGLE,
          source: 'OneTap',
        },
      });
    },
    onError: () => {},
  });

  const [
    registerUserWithToken,
  ] = useMutation(registerWithTokenMutation, {
    onCompleted: async (_, mutationOptions) => {
      await createSession({
        variables: {
          accessToken: mutationOptions?.variables?.accessToken,
          provider: Provider.GOOGLE,
        },
      });
    },
    onError: async (error, mutationOptions) => {
      if (error.message === 'Email has already been registered') {
        await createSession({
          variables: {
            accessToken: mutationOptions?.variables?.accessToken,
            provider: Provider.GOOGLE,
          },
        });
      }
    },
  });

  const onSuccess = useCallback((response: CredentialResponse) => {
    const { credential } = response;
    registerUserWithToken({
      variables: {
        accessToken: credential,
        provider: Provider.GOOGLE,
        registeredVia: Registrar.PWA,
      },
      context: {
        passErrors: ['Email has already been registered'],
      },
    });
  }, [registerUserWithToken]);

  const onError = useCallback(
    () => enqueueSnackbar(
      'Error fetching Google credentials',
      { variant: 'error' },
    ),
    [],
  );

  return (
    <GoogleOAuthProvider
      clientId={providers.google.properties.client_id as string}
      nonce="nonce"
    >
      <GoogleOneTapLogin
        onSuccess={onSuccess}
        onError={onError}
      />
    </GoogleOAuthProvider>
  );
};

export default GoogleOneTapProvider;
